import { useState, useEffect } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  TextField,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ResponsiveDialog from "../UI/ResponsiveDialog";
import { useMutation } from "@apollo/client";
import {
  UPDATE_TRANSFER_REQUEST,
  GET_TRANSFER_REQUESTS_BY_PLAYER_ACCEPTED_OR_TEAM_ACCEPTED,
  GET_TRANSFER_REQUESTS_BY_PLAYER_ACCEPTED_AND_TEAM_ACCEPTED,
} from "../../GraphQL/TransferRequests";
import useNotification from "../../hooks/useNotification.js";
import PlayerSelect from "../Player/PlayerSelect";
import TeamSelect from "../Team/TeamSelect";

const EditTransferRequestDialog = (props) => {
  const [transferRequest, setTransferRequest] = useState("");
  const [player, setPlayer] = useState("");
  const [team, setTeam] = useState("");
  const { notifySuccess } = useNotification();

  useEffect(() => {
    setTransferRequest(props.transferRequest);
  }, [props]);

  const [updateTransferRequest, { loading, error }] = useMutation(
    UPDATE_TRANSFER_REQUEST,
    {
      variables: {
        id: props.transferRequest.id,
        playerId: player,
        teamId: team,
        playerAccepted: transferRequest.playerAccepted,
        teamAccepted: transferRequest.teamAccepted,
        requestDate: transferRequest.requestDate,
      },
      refetchQueries: [
        GET_TRANSFER_REQUESTS_BY_PLAYER_ACCEPTED_OR_TEAM_ACCEPTED,
        GET_TRANSFER_REQUESTS_BY_PLAYER_ACCEPTED_AND_TEAM_ACCEPTED,
      ],
      onCompleted: () => { notifySuccess("Transfer request updated."); props.setOpen(false); },
    }
  );

  return (
    <div>
      <ResponsiveDialog
        open={props.open}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            updateTransferRequest();
          } else if (e.key === "Escape") {
            props.setOpen(false);
          }
        }}
        title="Transfer Request"
        onClose={() => props.setOpen(false)}
      >
        <DialogContent>
          <PlayerSelect
            player={player}
            setPlayer={setPlayer}
            players={props.players}
          />
          <TeamSelect teams={props.teams} team={team} setTeam={setTeam} />
          <FormControlLabel
            label="Player Accepted"
            control={
              <Checkbox
                data-testid="playerAccepted"
                autoFocus
                margin="dense"
                id="playerAccepted"
                label="Player Accepted"
                variant="standard"
                defaultChecked={props.transferRequest.playerAccepted}
                onChange={(e) => {
                  setTransferRequest({
                    ...transferRequest,
                    playerAccepted: e.target.checked,
                  });
                }}
              />
            }
          />
          <FormControlLabel
            label="Team Accepted"
            control={
              <Checkbox
                data-testid="teamAccepted"
                margin="dense"
                id="teamAccepted"
                label="Team Accepted"
                variant="standard"
                defaultChecked={props.transferRequest.teamAccepted}
                onChange={(e) => {
                  setTransferRequest({
                    ...transferRequest,
                    teamAccepted: e.target.checked,
                  });
                }}
              />
            }
          />
          <TextField
            data-testid="requestDate"
            margin="dense"
            id="requestDate"
            label="Request Date"
            type="date"
            fullWidth
            variant="standard"
            defaultValue={props.transferRequest.requestDate}
            onChange={(e) => {
              setTransferRequest({
                ...transferRequest,
                requestDate: e.target.value,
              });
            }}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        {error && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            {error.message}
          </Alert>
        )}
        <DialogActions>
          <Button
            data-testid="saveEditTransferRequestDialogButton"
            variant="contained"
            onClick={() => updateTransferRequest()}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "inherit" }} />
            ) : (
              "Update"
            )}
          </Button>
          <Button
            data-testid="closeEditTransferRequestDialogButton"
            onClick={() => props.setOpen(false)}
          >
            Cancel
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </div>
  );
};

export default EditTransferRequestDialog;
